/**
 * ComplianceAssistantWidget — Phase 7.3 + 7.4.
 *
 * Real-data AI compliance analyst. Wires three endpoints:
 *   GET  /api/compliance-ai/analyze
 *   GET  /api/compliance-ai/recommendations
 *   POST /api/compliance-ai/chat
 *
 * Renders: risk badge · summary tiles · risk list · top recommendations
 * · inline chat. When Emergent LLM key is present the chat is powered by
 * gemini-2.5-flash, otherwise a deterministic rule-based responder is used.
 */
import { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Bot, Loader2, AlertTriangle, Lightbulb, MessageSquare, Send, RefreshCw, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const RISK_STYLES = {
  low:      'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
  medium:   'bg-amber-500/10  text-amber-600  border-amber-500/30',
  high:     'bg-orange-500/10 text-orange-600 border-orange-500/30',
  critical: 'bg-red-500/10    text-red-600    border-red-500/30',
};

const PRIORITY_STYLES = {
  critical: 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30',
  high:     'bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/30',
  medium:   'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
  low:      'bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/30',
};

function fmtEur(v) {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1_000_000) return `€${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000)     return `€${(n / 1_000).toFixed(1)}k`;
  return `€${n.toFixed(0)}`;
}

export default function ComplianceAssistantWidget() {
  const [analysis, setAnalysis] = useState(null);
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);   // [{q, a, engine}]

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, r] = await Promise.all([
        api.get('/compliance-ai/analyze'),
        api.get('/compliance-ai/recommendations'),
      ]);
      setAnalysis(a.data);
      setRecs(r.data?.recommendations || []);
    } catch (err) {
      // 400 → no org yet: render an empty state
      setAnalysis({
        summary: { total_exposure: 0, risk_level: 'low', total_products: 0,
                   total_suppliers: 0, verified_evidence: 0, pending_evidence: 0,
                   products_with_gaps: 0 },
        risks: [], top_exposure_products: [], top_risk_suppliers: [], missing_evidence: [],
      });
      setRecs([]);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const ask = async () => {
    const q = question.trim();
    if (q.length < 3) { toast.error('Ask a fuller question'); return; }
    setChatBusy(true);
    try {
      const r = await api.post('/compliance-ai/chat', { question: q });
      setChatHistory((h) => [...h, { q, a: r.data.answer, engine: r.data.engine }]);
      setQuestion('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'AI request failed');
    } finally { setChatBusy(false); }
  };

  const suggested = [
    'What is my CBAM risk?',
    'Which products have missing evidence?',
    'Who is my highest-risk supplier?',
    'What should I do next?',
  ];

  const s = analysis?.summary;
  const riskStyle = s ? (RISK_STYLES[s.risk_level] || RISK_STYLES.low) : RISK_STYLES.low;
  const topRecs = recs.slice(0, 3);

  return (
    <Card className="p-0 overflow-hidden" data-testid="wgt-compliance-ai">
      <div className="bg-gradient-to-r from-emerald-700 via-emerald-800 to-teal-800 text-white px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
            <Bot className="w-4 h-4" />
          </div>
          <div>
            <div className="font-heading font-semibold text-sm">Veridex AI Compliance Analyst</div>
            <div className="text-[11px] opacity-80">Real-data risks · recommendations · Q&amp;A</div>
          </div>
        </div>
        <button onClick={load} className="text-[11px] opacity-80 hover:opacity-100 flex items-center gap-1"
                data-testid="compliance-ai-refresh-btn">
          <RefreshCw className="w-3 h-3" /> refresh
        </button>
      </div>

      <div className="p-5 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Analysing your org…
          </div>
        ) : s ? (
          <>
            {/* Summary tiles */}
            <div className="grid grid-cols-4 gap-2" data-testid="compliance-ai-summary">
              <div className="rounded-md border p-2 text-center">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">CBAM</div>
                <div className="font-heading font-bold text-sm mt-0.5">{fmtEur(s.total_exposure)}</div>
                <span className={`inline-block mt-1 text-[9px] px-1.5 py-0.5 rounded-full border ${riskStyle}`}>
                  {s.risk_level}
                </span>
              </div>
              <div className="rounded-md border p-2 text-center">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Products</div>
                <div className="font-heading font-bold text-sm mt-0.5">{s.total_products}</div>
                <div className="text-[9px] text-red-600 mt-1">{s.products_with_gaps} gaps</div>
              </div>
              <div className="rounded-md border p-2 text-center">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Suppliers</div>
                <div className="font-heading font-bold text-sm mt-0.5">{s.total_suppliers}</div>
                <div className="text-[9px] text-orange-600 mt-1">
                  {(analysis.top_risk_suppliers || []).length} high risk
                </div>
              </div>
              <div className="rounded-md border p-2 text-center">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Evidence</div>
                <div className="font-heading font-bold text-sm mt-0.5">
                  {s.total_evidence > 0
                    ? <>{s.verified_evidence}<span className="text-muted-foreground text-[10px]">/{s.total_evidence}</span></>
                    : <span className="text-muted-foreground">—</span>}
                </div>
                <div className="text-[9px] text-amber-600 mt-1">
                  {s.pending_evidence > 0 ? `${s.pending_evidence} pending` : 'no pending'}
                </div>
              </div>
            </div>

            {/* Risks */}
            {analysis.risks?.length > 0 && (
              <div className="space-y-1.5" data-testid="compliance-ai-risks">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Risks
                </div>
                {analysis.risks.slice(0, 2).map((r) => (
                  <div key={r.area} className={`rounded-md border px-2.5 py-1.5 text-xs ${PRIORITY_STYLES[r.severity] || PRIORITY_STYLES.medium}`}
                       data-testid={`compliance-ai-risk-${r.area.toLowerCase().replace(/\s+/g, '-')}`}>
                    <div className="font-medium">{r.area}</div>
                    <div className="text-[11px] opacity-90 mt-0.5">{r.description}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Recommendations */}
            {topRecs.length > 0 && (
              <div className="space-y-1.5" data-testid="compliance-ai-recommendations">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                  <Lightbulb className="w-3 h-3" /> Recommended actions
                </div>
                {topRecs.map((r) => (
                  <div key={r.id} className="rounded-md border p-2.5 hover:bg-muted/40 transition"
                       data-testid={`compliance-ai-rec-${r.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded border ${PRIORITY_STYLES[r.priority]}`}>
                            {r.priority}
                          </span>
                          <span className="font-medium text-sm truncate">{r.title}</span>
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">{r.description}</div>
                        {r.impact && (
                          <div className="text-[11px] text-emerald-600 mt-1 font-medium">
                            <ChevronRight className="w-3 h-3 inline -ml-0.5" />{r.impact}
                          </div>
                        )}
                      </div>
                      <Badge variant="outline" className="text-[9px] shrink-0">{r.confidence}%</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Chat */}
            <div className="border-t pt-3">
              <Button
                variant="outline" size="sm"
                onClick={() => setChatOpen((v) => !v)}
                data-testid="compliance-ai-chat-toggle"
                className="w-full justify-center"
              >
                <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                {chatOpen ? 'Close chat' : 'Ask Veridex AI'}
              </Button>

              {chatOpen && (
                <div className="mt-3 space-y-2" data-testid="compliance-ai-chat">
                  {chatHistory.length === 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {suggested.map((s2) => (
                        <button key={s2}
                                onClick={() => setQuestion(s2)}
                                className="text-[11px] px-2 py-1 rounded-full border hover:bg-muted"
                                data-testid={`compliance-ai-suggest-${s2.substring(0, 15).replace(/\s/g, '-')}`}>
                          {s2}
                        </button>
                      ))}
                    </div>
                  )}
                  {chatHistory.length > 0 && (
                    <div className="max-h-56 overflow-y-auto space-y-2 pr-1" data-testid="compliance-ai-history">
                      {chatHistory.map((h, i) => (
                        <div key={i} className="space-y-1">
                          <div className="text-xs font-medium text-right"><span className="bg-emerald-50 dark:bg-emerald-900/20 px-2 py-1 rounded">{h.q}</span></div>
                          <div className="text-xs bg-muted/60 rounded px-2 py-1.5 whitespace-pre-wrap" data-testid={`compliance-ai-answer-${i}`}>
                            {h.a}
                            <div className="text-[9px] text-muted-foreground mt-1">via {h.engine}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-1.5">
                    <Input
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !chatBusy) ask(); }}
                      placeholder="Ask about compliance…"
                      className="text-sm h-9"
                      data-testid="compliance-ai-input"
                    />
                    <Button
                      size="sm" onClick={ask} disabled={chatBusy || question.trim().length < 3}
                      data-testid="compliance-ai-send-btn"
                    >
                      {chatBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>
    </Card>
  );
}
